import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useToast } from "../../components/ui/Toast";
import { SoftButton, SoftFooter, cls } from "../hatim/soft/SoftKit";
import QuestionRows from "../muzakere/QuestionRows";
import { useDecideProposalMutation, useProposalQuery, useWithdrawProposalMutation } from "../../api/libraryApi";
import {
  PROPOSAL_STATUS_KEY,
  REPLY_MAX,
  changedParts,
  defaultChoices,
  diffSummaryText,
  formatDay,
} from "./libraryRules";
import {
  CenterMessage,
  ConfirmDialog,
  LIBRARY_HOME,
  LibraryPanel,
  MessageDialog,
  SectionTitle,
  Segment,
  Spinner,
  packagePath,
} from "./LibraryKit";

const CHOICES = [
  { value: "proposal", key: "lib_diff_proposed" },
  { value: "current", key: "lib_diff_current" },
];

const PART_KEY = {
  type: "lib_part_type",
  text: "lib_part_text",
  options: "lib_part_options",
  correct: "lib_part_correct",
};

function MetaRow({ label, current, proposed }) {
  const { t } = useScriptMode();
  return (
    <li className={`px-4 py-2.5 flex flex-col gap-0.5 ${cls.row}`}>
      <span className={`text-xs ${cls.muted}`}>{label}</span>
      <span className={`text-sm line-through decoration-1 ${cls.muted}`}>{`${t("lib_diff_current")}: ${current || "..."}`}</span>
      <span className={`text-sm ${cls.title}`}>{`${t("lib_diff_proposed")}: ${proposed || "..."}`}</span>
    </li>
  );
}

/**
 * Öneri (mobil library/proposal/[id]): asıl paketin güncel haline göre fark;
 * eklenen, çıkarılan, değişen sorular ve bilgi değişiklikleri. Çakışan
 * soruda asıl sahip hangisinin kalacağını seçer; Kabul Et ya da Reddet
 * (isteğe bağlı cevapla). Öneren açık öneriyi geri çeker.
 */
export default function ProposalPage() {
  const { id } = useParams();
  const { t } = useScriptMode();
  const toast = useToast();
  const { data, isLoading, isError, error, refetch } = useProposalQuery({ proposalId: id });
  const [decide, { isLoading: deciding }] = useDecideProposalMutation();
  const [withdraw, { isLoading: withdrawing }] = useWithdrawProposalMutation();
  const [choices, setChoices] = useState({});
  const [decision, setDecision] = useState(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const busy = deciding || withdrawing;
  const proposal = data?.proposal;
  const diff = data?.diff;

  // Her yüklemede çakışan sorular varsayılan olarak "Önerilen".
  useEffect(() => {
    if (diff) setChoices(defaultChoices(diff));
  }, [diff]);

  if (isLoading) {
    return (
      <LibraryPanel title={t("lib_proposal")} back={LIBRARY_HOME}>
        <Spinner />
      </LibraryPanel>
    );
  }
  if (isError || !proposal) {
    return (
      <LibraryPanel title={t("lib_proposal")} back={LIBRARY_HOME}>
        <CenterMessage text={error?.message || t("lib_proposal_failed")} danger onRetry={refetch} error={error} />
      </LibraryPanel>
    );
  }

  const back = proposal.isMine && proposal.forkId ? packagePath(proposal.forkId) : proposal.targetId ? packagePath(proposal.targetId) : LIBRARY_HOME;
  const open = proposal.status === "open";
  const canDecide = open && proposal.canDecide;
  const canWithdraw = open && proposal.isMine && !proposal.canDecide;

  const send = async (message) => {
    const action = decision;
    const result = await decide({ proposalId: proposal.id, action, message, diff, choices });
    if (result.error) {
      toast.error(result.error.message || t("lib_action_failed"));
      return;
    }
    setDecision(null);
    toast.success(t(action === "accept" ? "lib_proposal_accepted" : "lib_proposal_rejected"));
  };

  const doWithdraw = async () => {
    const result = await withdraw({ proposalId: proposal.id });
    setConfirmWithdraw(false);
    if (result.error) toast.error(result.error.message || t("lib_action_failed"));
    else toast.success(t("lib_proposal_withdrawn"));
  };

  const footer =
    canDecide || canWithdraw ? (
      <SoftFooter>
        {canDecide ? (
          <>
            <SoftButton tone="cancel" fullWidth onClick={() => setDecision("reject")} disabled={busy}>
              {t("lib_reject")}
            </SoftButton>
            <SoftButton tone="success" fullWidth startIcon={<CheckIcon />} onClick={() => setDecision("accept")} disabled={busy}>
              {t("lib_accept")}
            </SoftButton>
          </>
        ) : (
          <SoftButton tone="cancel" fullWidth onClick={() => setConfirmWithdraw(true)} disabled={busy}>
            {t("lib_withdraw")}
          </SoftButton>
        )}
      </SoftFooter>
    ) : null;

  return (
    <LibraryPanel title={t("lib_proposal")} back={back} footer={footer}>
      <div className="px-4 pt-4 pb-3 flex flex-col gap-1.5">
        <h2 className={`text-xl font-Medium leading-snug break-words ${cls.title}`}>
          {proposal.targetTitle || proposal.forkTitle || t("lib_proposal")}
        </h2>
        <p className={`text-sm ${cls.muted}`}>
          {[proposal.proposerName, t(PROPOSAL_STATUS_KEY[proposal.status] || "lib_prop_open"), formatDay(proposal.createdAt)]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <div className={`flex flex-wrap gap-x-4 gap-y-1 text-sm ${cls.accent}`}>
          {proposal.targetId ? (
            <Link to={packagePath(proposal.targetId)} className="hover:underline">
              {t("lib_open_original")}
            </Link>
          ) : null}
          {proposal.forkId ? (
            <Link to={packagePath(proposal.forkId)} className="hover:underline">
              {t("lib_open_fork")}
            </Link>
          ) : null}
        </div>
        {proposal.message ? <p className={`text-base whitespace-pre-line break-words ${cls.title}`}>{proposal.message}</p> : null}
        {proposal.reason ? <p className={`text-sm ${cls.muted}`}>{`${t("lib_reply")}: ${proposal.reason}`}</p> : null}
      </div>

      <div className={`px-4 py-3 ${cls.summary} border-t border-terakkiBorder dark:border-white/[0.14]`}>
        {!diff ? (
          <span className={`flex items-center gap-2 text-sm ${cls.title}`}>
            <LockOutlinedIcon sx={{ fontSize: 18 }} className={cls.accent} aria-hidden />
            {t("mzq_lock_reason")}
          </span>
        ) : (
          <span className={`text-sm ${cls.title}`}>{diffSummaryText(diff, t)}</span>
        )}
      </div>

      {diff && Object.keys(diff.meta).length ? (
        <>
          <SectionTitle>{t("lib_diff_meta")}</SectionTitle>
          <ul className="tk-stagger">
            {diff.meta.title ? (
              <MetaRow label={t("lib_field_title")} current={diff.meta.title.current} proposed={diff.meta.title.proposed} />
            ) : null}
            {diff.meta.description ? (
              <MetaRow
                label={t("lib_field_description_short")}
                current={diff.meta.description.current}
                proposed={diff.meta.description.proposed}
              />
            ) : null}
            {diff.meta.tags ? (
              <MetaRow
                label={t("lib_field_tags_short")}
                current={diff.meta.tags.current.join(", ")}
                proposed={diff.meta.tags.proposed.join(", ")}
              />
            ) : null}
          </ul>
        </>
      ) : null}

      {diff?.added.length ? (
        <>
          <SectionTitle>{`${t("lib_diff_added")} · ${diff.added.length}`}</SectionTitle>
          <div className="px-3 border-l-2 border-[#2E9E6A]/50 ml-4 mt-2">
            <QuestionRows questions={diff.added} showAnswers />
          </div>
        </>
      ) : null}

      {diff?.removed.length ? (
        <>
          <SectionTitle>{`${t("lib_diff_removed")} · ${diff.removed.length}`}</SectionTitle>
          <div className="px-3 border-l-2 border-terakkiError/50 ml-4 mt-2 opacity-80">
            <QuestionRows questions={diff.removed} showAnswers />
          </div>
        </>
      ) : null}

      {diff?.changed.length ? (
        <>
          <SectionTitle>{`${t("lib_diff_changed")} · ${diff.changed.length}`}</SectionTitle>
          <ul className="tk-stagger">
            {diff.changed.map((item) => {
              const parts = changedParts(item.current, item.proposed);
              return (
                <li key={item.qid} className={`px-4 py-3 flex flex-col gap-2 ${cls.row}`}>
                  {parts.length ? (
                    <span className={`text-xs ${cls.muted}`}>
                      {`${t("lib_diff_changed_parts")}: ${parts.map((p) => t(PART_KEY[p])).join(", ")}`}
                    </span>
                  ) : null}
                  {item.conflict ? (
                    <div className="flex flex-col gap-2">
                      <span className={`flex items-center gap-1.5 text-sm ${cls.warning}`}>
                        <ErrorOutlineIcon sx={{ fontSize: 17 }} aria-hidden />
                        {t("lib_conflict")}
                      </span>
                      <span className={`text-xs ${cls.muted}`}>{t("lib_conflict_hint")}</span>
                      {canDecide ? (
                        <Segment
                          small
                          value={choices[item.qid] || "proposal"}
                          options={CHOICES}
                          label={t("lib_conflict")}
                          onChange={(value) => setChoices((prev) => ({ ...prev, [item.qid]: value }))}
                        />
                      ) : null}
                    </div>
                  ) : null}
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="min-w-0">
                      <span className={`text-xs ${cls.muted}`}>{t("lib_diff_current")}</span>
                      <QuestionRows questions={[item.current]} showAnswers />
                    </div>
                    <div className="min-w-0">
                      <span className={`text-xs ${cls.accent}`}>{t("lib_diff_proposed")}</span>
                      <QuestionRows questions={[{ ...item.proposed, qid: `${item.qid}-p` }]} showAnswers />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      <MessageDialog
        open={decision === "reject"}
        title={t("lib_reject")}
        hint={t("lib_reject_hint")}
        placeholder={t("lib_reply_optional")}
        sendLabel={t("lib_reject")}
        danger
        maxLength={REPLY_MAX}
        busy={busy}
        onSend={send}
        onClose={() => setDecision(null)}
      />
      <MessageDialog
        open={decision === "accept"}
        title={t("lib_accept")}
        hint={t("lib_accept_hint")}
        placeholder={t("lib_reply_optional")}
        sendLabel={t("lib_accept")}
        maxLength={REPLY_MAX}
        busy={busy}
        onSend={send}
        onClose={() => setDecision(null)}
      />
      <ConfirmDialog
        open={confirmWithdraw}
        title={t("lib_withdraw")}
        text={t("lib_withdraw_text")}
        confirmLabel={t("lib_withdraw")}
        danger
        busy={busy}
        onConfirm={doWithdraw}
        onClose={() => setConfirmWithdraw(false)}
      />
    </LibraryPanel>
  );
}
